import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Tag,
  Typography,
  Switch,
  Alert,
} from 'antd';
import {
  ReloadOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { botAPI } from '../services/api';

const { TextArea } = Input;
const { Title, Text } = Typography;

interface BotConfig {
  id: number;
  bot_name: string;
  bot_username: string;
  bot_type: string;
  join_message: string | null;
  status: number;
}

const BotJoinMessage: React.FC = () => {
  const [bots, setBots] = useState<BotConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBot, setEditingBot] = useState<BotConfig | null>(null);
  const [form] = Form.useForm();

  const fetchBots = async () => {
    setLoading(true);
    try {
      const response = await botAPI.getAll();
      setBots(response.data);
    } catch {
      message.error('获取机器人列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBots(); }, []);

  const openModal = (bot: BotConfig) => {
    setEditingBot(bot);
    form.setFieldsValue({
      joinMessage: bot.join_message || '',
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values: { joinMessage: string }) => {
    if (!editingBot) return;
    try {
      await botAPI.update(editingBot.id, {
        joinMessage: values.joinMessage || null,
      });
      message.success('入群自我介绍已更新');
      setModalVisible(false);
      form.resetFields();
      fetchBots();
    } catch (error: any) {
      message.error(error.response?.data?.error || '操作失败');
    }
  };

  const handleClear = async (bot: BotConfig) => {
    try {
      await botAPI.update(bot.id, { joinMessage: null });
      message.success('已清除自我介绍，将使用默认消息');
      fetchBots();
    } catch {
      message.error('操作失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '机器人', dataIndex: 'bot_name', width: 160,
      render: (name: string, record: BotConfig) => (
        <span>
          {name}
          {record.bot_username && (
            <Text type="secondary" style={{ marginLeft: 4, fontSize: 12 }}>
              @{record.bot_username}
            </Text>
          )}
        </span>
      ),
    },
    {
      title: '类型', dataIndex: 'bot_type', width: 100,
      render: (type: string) => (
        <Tag color={type === 'group_management' ? 'blue' : 'green'}>
          {type === 'group_management' ? '群管' : '记账'}
        </Tag>
      ),
    },
    {
      title: '入群自我介绍', dataIndex: 'join_message', ellipsis: true,
      render: (text: string | null) => (
        text
          ? <Text ellipsis title={text}>{text}</Text>
          : <Text type="secondary">未配置（使用默认消息）</Text>
      ),
    },
    {
      title: '状态', dataIndex: 'join_message', width: 100,
      key: 'status',
      render: (text: string | null) => (
        text
          ? <Tag icon={<CheckCircleOutlined />} color="success">已配置</Tag>
          : <Tag icon={<CloseCircleOutlined />} color="default">默认</Tag>
      ),
    },
    {
      title: '操作', width: 180, fixed: 'right' as const,
      render: (_: any, record: BotConfig) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(record)}>
            编辑
          </Button>
          {record.join_message && (
            <Button size="small" danger onClick={() => handleClear(record)}>
              清除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={3}>入群自我介绍管理</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          配置机器人被拉入群组时发送的自我介绍消息，支持 HTML 格式和变量替换
        </Text>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchBots}>刷新</Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={bots}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 800 }}
      />

      <Modal
        title={editingBot ? `编辑入群自我介绍 - ${editingBot.bot_name}` : '编辑入群自我介绍'}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); form.resetFields(); }}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Alert
            message="支持的变量和格式"
            description={
              <div style={{ fontSize: 12, lineHeight: '22px' }}>
                <div>
                  <Text code>{'{bot_name}'}</Text> 机器人名称
                </div>
                <div style={{ marginTop: 4 }}>
                  支持 HTML 格式：<Text code>{'<b>粗体</b>'}</Text> <Text code>{'<i>斜体</i>'}</Text> <Text code>{'<a href="...">链接</a>'}</Text>
                </div>
              </div>
            }
            type="info"
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            label="自我介绍内容"
            name="joinMessage"
            extra="留空则使用默认自我介绍消息"
          >
            <TextArea
              rows={5}
              placeholder={'大家好，我是<b>{bot_name}</b>\n感谢把我加入贵群！\n\n有任何问题请随时联系管理员。'}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BotJoinMessage;
